/* eslint
    @typescript-eslint/no-shadow: "warn",
    default-case: "warn",
    prefer-template: "warn",
    block-scoped-var: "warn",
    no-redeclare: "warn",
*/

import { EasyfyEventLogger } from "@shared/easyfy.events/easyfy.event.devlogger";
import { EasyfyEventData, IDataMapper, IEasyfyEvent, IPageViewData, IQuantityChangedData, ISearchPerformedData, ISessionStarted } from "@shared/easyfy.events/easyfy.eventdata";
import { EventNames } from "@shared/easyfy.events/easyfy.eventhandler";
import { event } from "../../easyfy.decorators";

/* Exporter class */
export class StatsDataMapper implements IDataMapper {

  MapEventData(e: IEasyfyEvent): any {
    let mappedEvent;

    switch (e.eventName.toLowerCase()) {
      case EventNames.SessionStarted: {
        mappedEvent = this.BuildSessionStartedEvent(e);
        break;
      }
      case EventNames.PageView: {
        mappedEvent = this.BuildPageViewEvent(e);
        break;
      }
      case EventNames.ProductPageView: {
        mappedEvent = this.BuildProductPageViewEvent(e);
        break;
      }
      case EventNames.SearchPerformed: {
        mappedEvent = this.BuildSearchPerformedEvent(e);
        break;
      }
      case EventNames.AddToCart: {
        if (e.data.isFirstAddForRow) {
          mappedEvent = this.BuildAddToBasketEvent(e);
        }
        else {
          EasyfyEventLogger.Log({ type: "warning", message: `Event '${e.eventName}' skipped for stats, conditions not met` });
        }
        break;
      }
      case EventNames.RemoveFromCart: {
        if (e.data.isLastRemoveForRow) {
          mappedEvent = this.BuildRemoveFromBasketEvent(e);
        }
        else {
          EasyfyEventLogger.Log({ type: "warning", message: `Event '${e.eventName}' skipped for stats, conditions not met` });
        }
        break;
      }
      case EventNames.QuantityChanged: {
        mappedEvent = this.BuildQuantityChangedEvent(e);
        break;
      }
      case EventNames.ProductImpression: {
        mappedEvent = this.BuildProductImpressionEvents(e);
        break;
      }
      default: {
        console.warn(`Event '${e.eventName}' not mapped for stats`);
      }
    }

    return mappedEvent;
  }

  BuildSessionStartedEvent(e: IEasyfyEvent): any {
    const data = e.data as ISessionStarted;
    return {
      type: "SessionStarted",
      TimeStampUtc: this.GetFormattedTimeStamp(),
      Culture: data.Culture,
      Currency: data.Currency,
      IsDesktop: data.IsDesktop,
      IsMobilePhone: data.IsMobilePhone,
      IsTablet: data.IsTablet,
      RecurringUser: data.RecurringUser,
      Referrer: data.Referrer,
      Path: data.Path,
      UserAgent: data.UserAgent
    };
  }

  BuildPageViewEvent(e: IEasyfyEvent): any {
    const data = e.data as IPageViewData;

    return {
      type: "PageViewed",
      Path: this.GetCurrentUrlPath(),
      PageId: data.pageId,
      TimeStampUtc: this.GetFormattedTimeStamp(),
    };
  }

  BuildProductPageViewEvent(e: IEasyfyEvent): any {
    if (!e.data.productIdentifier) throw new Error('Product identifier missing');
    const product = EasyfyEventData.Product(e.data.productIdentifier);

    return {
      type: "ProductViewed",
      ProductId: product.DomainId,
      Path: this.GetCurrentUrlPath(),
      Referrer: document.referrer,
      TimeStampUtc: this.GetFormattedTimeStamp(),
    };
  }

  BuildSearchPerformedEvent(e: IEasyfyEvent): any {
    const data = e.data as ISearchPerformedData;

    return {
      Path: this.GetCurrentUrlPath(),
      SearchWord: data.searchString,
      TimeStampUtc: this.GetFormattedTimeStamp(),
      type: "UserSearched",
    };
  }

  BuildAddToBasketEvent(e: IEasyfyEvent): any {
    if (!e.data.productIdentifier) throw new Error('Product identifier missing');

    const product = EasyfyEventData.Product(e.data.productIdentifier);
    const productListIndex = EasyfyEventData.ProductIndexInList(e.data.listName, product.Id) + 1;
    const pageInfo = EasyfyEventData.PageInfo();

    return {
      type: "ProductAddedToBasket",
      Path: this.GetCurrentUrlPath(),
      TimeStampUtc: this.GetFormattedTimeStamp(),
      ProductId: product.DomainId,
      Quantity: e.data.quantity,
      LineNumber: e.data.rowId,
      ListPosition: productListIndex,
      ListType: e.data.listName,
      Origin: pageInfo.PageType,
      View: pageInfo.ViewName
    };
  }

  BuildRemoveFromBasketEvent(e: IEasyfyEvent): any {
    const product = EasyfyEventData.Product(e.data.productIdentifier);

    return {
      type: "ProductRemovedFromBasket",
      Path: this.GetCurrentUrlPath(),
      TimeStampUtc: this.GetFormattedTimeStamp(),
      Sku: product.Id,
      LineNumber: e.data.rowId
    };
  }

  BuildQuantityChangedEvent(e: IEasyfyEvent): any {
    const data = e.data as IQuantityChangedData;
    const product = EasyfyEventData.Product(e.data.productIdentifier);

    return {
      type: "ProductQuantityChanged",
      TimeStampUtc: this.GetFormattedTimeStamp(),
      NewQuantity: data.newQuantity,
      OldQuantity: data.oldQuantity,
      Sku: product.Id,
      LineNumber: e.data.rowId,
      Url: this.GetCurrentUrlPath()
    };
  }

  BuildProductImpressionEvents(e: IEasyfyEvent): any[] {
    if (!e.data.productIdentifiers) { throw new Error('Product identifiers missing'); }
    if (!e.data.salesUnitCodes) { throw new Error('Product sales unit codes missing'); }
    if (e.data.salesUnitCodes.length !== e.data.productIdentifiers.length) { throw new Error('Product sales unit codes and identifiers count missmatch'); }
    const priceIds: string[] = [];
    const productImpressions: any[] = [];

    for (let i = 0; i < e.data.productIdentifiers.length; i++) {
      priceIds.push(`${e.data.productIdentifiers[i]}-${e.data.salesUnitCodes[i]}`);
    }

    const eventProducts = EasyfyEventData.Products(e.data.productIdentifiers);

    for (let i = 0; i < eventProducts.length; i++) {
      const product = eventProducts[i];

      const index = eventProducts.indexOf(product);
      const productListIndex = EasyfyEventData.ProductIndexInList(e.data.listIds[index], product.Id) + 1;

      productImpressions.push({
        type: "ProductImpression",
        TimeStampUtc: this.GetFormattedTimeStamp(),
        ListPosition: productListIndex,
        ListType: e.data.listIds[index],
        Path: this.GetCurrentUrlPath(),
        ProductId: product.DomainId,
      });
    }

    return productImpressions;
  }

  GetCurrentUrlPath(): string {
    return window.location.pathname;
  }

  GetFormattedTimeStamp(): string {
    const date = (new Date).toISOString();
    return date;
  }
}
